import React, { useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

export default function Navbar(): React.JSX.Element {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { isAuthenticated, user, logout } = useAuth();
  const userName = user?.application?.firstName || user?.name || user?.email || 'Account';

  const handleSignOut = () => {
    setIsMobileMenuOpen(false);
    void logout().catch(() => undefined);
  };

  const desktopLinkClasses = ({ isActive }: { isActive: boolean }) =>
    `text-sm font-medium transition-colors hover:text-mcc-neutral ${
      isActive ? 'text-mcc-neutral font-semibold border-b-2 border-mcc-light pb-0.5' : 'text-mcc-darkest'
    }`;

  const mobileLinkClasses = ({ isActive }: { isActive: boolean }) =>
    `block rounded-lg px-4 py-3 text-base font-semibold transition-colors ${
      isActive ? 'bg-mcc-lightest/30 text-mcc-neutral font-bold' : 'text-mcc-darkest hover:bg-slate-50'
    }`;

  return (
    <nav className="sticky top-0 z-50 w-full border-b border-mcc-lightest/40 bg-white/95 backdrop-blur-md shadow-sm">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 md:px-6">
        {/* Brand Logo / Home link */}
        <Link to="/" className="flex items-center gap-3">
          <img
            src="/MCC_Logo.svg"
            alt="My Connective Care LLC"
            className="h-10 w-auto object-contain"
          />
        </Link>

        {/* Desktop Navigation Links (Hidden on Mobile) */}
        <div className="hidden items-center space-x-8 md:flex">
          <NavLink to="/" className={desktopLinkClasses}>Home</NavLink>
          <NavLink to="/about" className={desktopLinkClasses}>About</NavLink>
          <NavLink to="/services" className={desktopLinkClasses}>Services</NavLink>
          <NavLink to="/contact" className={desktopLinkClasses}>Contact</NavLink>
        </div>

        {/* Action Buttons & Mobile Hamburger */}
        <div className="flex items-center gap-3">
          {/* LOGGED IN VIEW (Desktop & Tablet) */}
          {isAuthenticated && user ? (
            <div className="hidden items-center gap-3 sm:flex">
              {/* Smaller Hello Greeting */}
              <span className="text-xs font-semibold text-slate-500">
                Hello, <strong className="text-mcc-darkest font-bold">{userName}</strong>
              </span>

              <Link
                to="/dashboard"
                className="rounded-md border border-mcc-darkest bg-white px-3.5 py-1.5 text-xs font-bold text-mcc-darkest transition-colors hover:bg-mcc-lightest/30 shadow-xs"
              >
                My Account
              </Link>

              <button
                type="button"
                onClick={handleSignOut}
                className="rounded-md border border-slate-300 bg-slate-50 px-3.5 py-1.5 text-xs font-semibold text-slate-700 transition-colors hover:bg-slate-100 hover:text-mcc-darkest"
              >
                Sign Out
              </button>
            </div>
          ) : (
            /* LOGGED OUT VIEW (Desktop & Tablet) */
            <div className="hidden items-center space-x-3 sm:flex">
              <Link
                to="/login"
                className="rounded-md border border-mcc-lightest bg-mcc-lightest/20 px-4 py-2 text-sm font-medium text-mcc-darkest transition-colors hover:bg-mcc-lightest/40 hover:border-mcc-light shadow-sm"
              >
                Log In
              </Link>
              <Link
                to="/signup"
                className="rounded-md bg-mcc-neutral px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-mcc-darkest shadow-md"
              >
                Get Care
              </Link>
            </div>
          )}

          {/* Mobile Hamburger Toggle Button */}
          <button
            type="button"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="rounded-lg p-2 text-mcc-darkest hover:bg-mcc-lightest/20 focus:outline-none md:hidden"
            aria-label="Toggle navigation menu"
          >
            {isMobileMenuOpen ? (
              /* Close "X" Icon */
              <svg className="h-6 w-6 stroke-current" fill="none" viewBox="0 0 24 24" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              /* Hamburger Menu Icon */
              <svg className="h-6 w-6 stroke-current" fill="none" viewBox="0 0 24 24" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
              </svg>
            )}
          </button>
        </div>
      </div>

      {/* Expanding Mobile Drawer */}
      {isMobileMenuOpen && (
        <div className="border-t border-mcc-lightest/40 bg-white px-4 pt-3 pb-6 space-y-2 md:hidden">
          <NavLink
            to="/"
            onClick={() => setIsMobileMenuOpen(false)}
            className={mobileLinkClasses}
          >
            Home
          </NavLink>
          <NavLink
            to="/about"
            onClick={() => setIsMobileMenuOpen(false)}
            className={mobileLinkClasses}
          >
            About
          </NavLink>
          <NavLink
            to="/services"
            onClick={() => setIsMobileMenuOpen(false)}
            className={mobileLinkClasses}
          >
            Services
          </NavLink>
          <NavLink
            to="/contact"
            onClick={() => setIsMobileMenuOpen(false)}
            className={mobileLinkClasses}
          >
            Contact
          </NavLink>

          {/* Mobile Action Controls */}
          {isAuthenticated && user ? (
            <div className="pt-4 space-y-3 sm:hidden border-t border-slate-100">
              <div className="text-center text-xs font-semibold text-slate-500">
                Hello, <strong className="text-mcc-darkest font-bold">{userName}</strong>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Link
                  to="/dashboard"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="rounded-xl border-2 border-mcc-darkest bg-white px-4 py-2.5 text-center text-xs font-bold text-mcc-darkest transition-colors hover:bg-mcc-lightest/30"
                >
                  My Account
                </Link>
                <button
                  type="button"
                  onClick={handleSignOut}
                  className="rounded-xl border border-slate-300 bg-slate-50 px-4 py-2.5 text-center text-xs font-bold text-slate-700 transition-colors hover:bg-slate-100"
                >
                  Sign Out
                </button>
              </div>
            </div>
          ) : (
            <div className="pt-4 grid grid-cols-2 gap-3 sm:hidden border-t border-slate-100">
              <Link
                to="/login"
                onClick={() => setIsMobileMenuOpen(false)}
                className="rounded-xl border border-mcc-lightest bg-mcc-lightest/20 px-4 py-3 text-center text-sm font-bold text-mcc-darkest transition-colors hover:bg-mcc-lightest/40"
              >
                Log In
              </Link>
              <Link
                to="/signup"
                onClick={() => setIsMobileMenuOpen(false)}
                className="rounded-xl bg-mcc-neutral px-4 py-3 text-center text-sm font-bold text-white transition-colors hover:bg-mcc-darkest shadow-md"
              >
                Get Care
              </Link>
            </div>
          )}
        </div>
      )}
    </nav>
  );
}
